/**
 * §3 roles → permissions. The restrictions column of §3 is encoded as absences in this matrix plus the
 * separation-of-duties and dual-control helpers. Super-admin access is just-in-time (JIT) with enhanced audit
 * and never includes routine winner selection.
 */
import { z } from 'zod';
export const PERMISSIONS = [
    // lottery configuration and lifecycle
    'lottery.read',
    'lottery.create',
    'lottery.update',
    'lottery.submit_review',
    'lottery.approve',
    'lottery.publish',
    'lottery.pause',
    'lottery.close',
    'lottery.cancel',
    'lottery.simulate',
    // catalogue
    'prizes.manage',
    'brands.manage',
    'sponsors.manage',
    'sampling.manage',
    'platforms.manage',
    'engagement_tasks.manage',
    // draws
    'draw.precheck',
    'draw.run',
    'draw.reveal',
    'draw.redraw',
    'draw.audit.read',
    // winners board
    'winners.view',
    'winners.assign',
    'winners.mark_messaged',
    'winners.lookup',
    // claims
    'claims.view',
    'claims.review',
    'claims.request_info',
    'claims.verification.record',
    'claims.approve',
    'claims.reject',
    'claims.appeal.decide',
    // finance
    'finance.refund',
    'finance.payout.approve',
    'finance.reconcile',
    'finance.ledger.read',
    'fulfillment.update',
    // audience, identity, reporting
    'audience.segments.read',
    'audience.export',
    'identity.unmask',
    'identity.evidence.read',
    'sponsor.report.view',
    'exports.read',
    'audit.read',
    // platform
    'platform.tenant.manage',
    'platform.provider.manage',
    'platform.flags.manage',
    'platform.incident.access',
];
export const Permission = z.enum(PERMISSIONS);
/** Permissions granted per role. Absences encode the §3 restrictions (see tests). */
export const ROLE_PERMISSIONS = {
    ORGANIZER_ADMIN: [
        'lottery.read',
        'lottery.create',
        'lottery.update',
        'lottery.submit_review',
        'lottery.simulate',
        'prizes.manage',
        'brands.manage',
        'sponsors.manage',
        'sampling.manage',
        'platforms.manage',
        'engagement_tasks.manage',
        'draw.precheck',
        'draw.audit.read',
        'winners.view',
        'winners.lookup',
        'claims.view',
        'audience.segments.read',
        'audience.export',
        'sponsor.report.view',
        'exports.read',
        'audit.read',
        // §3: cannot approve own high-value publication (see assertSeparationOfDuties), set winners (no draw.run /
        // draw.redraw) or export unrestricted identity evidence (no identity.evidence.read / identity.unmask).
    ],
    LOTTERY_APPROVER: [
        'lottery.read',
        'lottery.approve',
        'lottery.publish',
        'lottery.pause',
        'lottery.cancel',
        'lottery.simulate',
        'draw.audit.read',
        'audit.read',
        // §3: cannot modify ledger history (no finance.*) or allocate prize units (no prizes.manage / draw.run).
    ],
    DRAW_OPERATOR: [
        'lottery.read',
        'draw.precheck',
        'draw.run',
        'draw.reveal',
        'draw.redraw',
        'draw.audit.read',
        'lottery.close',
        // §3: no rule editing (no lottery.update); high-value reveals need a second operator (dual control).
    ],
    ENGAGEMENT_ADMIN: [
        'lottery.read',
        'winners.view',
        'winners.assign',
        'winners.mark_messaged',
        'winners.lookup',
        'claims.view',
        'engagement_tasks.manage',
        'platforms.manage',
        'audience.segments.read',
        // §3: no unrestricted ID or bank access; no claim approval (claims.approve) or payout rights.
    ],
    CLAIMS_REVIEWER: [
        'lottery.read',
        'winners.view',
        'winners.lookup',
        'claims.view',
        'claims.review',
        'claims.request_info',
        'claims.verification.record',
        'claims.approve',
        'claims.reject',
        'claims.appeal.decide',
        'identity.evidence.read',
        'identity.unmask',
        // §3: no payment approval (finance.*) or configuration rights (lottery.update etc.).
    ],
    FINANCE: [
        'lottery.read',
        'finance.refund',
        'finance.payout.approve',
        'finance.reconcile',
        'finance.ledger.read',
        'fulfillment.update',
        'claims.view',
        'winners.view',
        'exports.read',
        'identity.unmask',
        // §3: no outcome or eligibility manipulation (no draw.*, no claims.approve, no lottery.update).
    ],
    SPONSOR_VIEWER: [
        'sponsor.report.view',
        // §3: read-only aggregate reporting on their own sponsorships (scoping enforced by sponsorId in session).
    ],
    PLATFORM_SUPER_ADMIN: [
        'platform.tenant.manage',
        'platform.provider.manage',
        'platform.flags.manage',
        'platform.incident.access',
        'audit.read',
        'lottery.read',
        'draw.audit.read',
        // §3: just-in-time privileged access with enhanced audit; no routine winner selection (no draw.run/reveal).
    ],
};
/** Permissions the super-admin never holds, even with JIT access (no routine winner selection, §3). */
export const SUPER_ADMIN_NEVER = [
    'draw.run',
    'draw.reveal',
    'draw.redraw',
    'claims.approve',
    'finance.payout.approve',
];
/** True when any of the roles grants the permission. Super-admin requires an active JIT grant. */
export const can = (roles, permission, ctx = {}) => {
    for (const role of roles) {
        if (role === 'PLATFORM_SUPER_ADMIN') {
            if (!ctx.superAdminJitActive)
                continue;
            if (SUPER_ADMIN_NEVER.includes(permission))
                continue;
        }
        if (ROLE_PERMISSIONS[role].includes(permission))
            return true;
    }
    return false;
};
export const permissionsFor = (roles, ctx = {}) => PERMISSIONS.filter((p) => can(roles, p, ctx));
/**
 * Actions that require two different people (maker/checker) — §3 "cannot approve own high-value publication",
 * §10.5 payouts above threshold, §9.5 two operators for high-value draws, §11.2 high-value claim dual approval.
 */
export const DUAL_CONTROL_ACTIONS = [
    'lottery.publish',
    'draw.reveal',
    'finance.payout.approve',
    'claims.approve',
    'payout.destination.change',
];
/** High-value concept for dual control: prize value or payout amount at or above the tenant threshold. */
export const isHighValue = (valueMinor, policy) => valueMinor >= policy.highValueThresholdMinor;
/** Paid lotteries and high-value lotteries need a second person for approval (§5.4). */
export const requiresDualControl = (input) => {
    if (input.action === 'lottery.publish' && input.paid)
        return true;
    if (input.action === 'payout.destination.change')
        return isHighValue(input.valueMinor, input.policy);
    return isHighValue(input.valueMinor, input.policy);
};
export class SeparationOfDutiesError extends Error {
    action;
    actorId;
    code = 'separation_of_duties';
    constructor(action, actorId) {
        super(`${action}: the same person (${actorId}) cannot perform both steps`);
        this.action = action;
        this.actorId = actorId;
        this.name = 'SeparationOfDutiesError';
    }
}
/** The maker (creator / submitter / first operator) and the checker must differ. */
export const assertSeparationOfDuties = (action, makerId, checkerId) => {
    if (makerId === checkerId)
        throw new SeparationOfDutiesError(action, checkerId);
};
/** Roles that may hold a tenant session (sponsor viewers are scoped to a sponsorId; super-admin is cross-tenant). */
export const TENANT_STAFF_ROLES = [
    'ORGANIZER_ADMIN',
    'LOTTERY_APPROVER',
    'DRAW_OPERATOR',
    'ENGAGEMENT_ADMIN',
    'CLAIMS_REVIEWER',
    'FINANCE',
];
/** Actions that must step up (re-authenticate with MFA) before executing (D-003, §15.1). */
export const STEP_UP_PERMISSIONS = [
    'draw.run',
    'draw.reveal',
    'draw.redraw',
    'finance.refund',
    'finance.payout.approve',
    'identity.unmask',
    'identity.evidence.read',
    'audience.export',
    'lottery.cancel',
    'platform.tenant.manage',
    'platform.provider.manage',
];
//# sourceMappingURL=rbac.js.map